import * as React from 'react';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Drawer, { drawerClasses } from '@mui/material/Drawer';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import MenuContent from './MenuContent';
import { SideMenuMobileProps } from '~/shared/types/types';
import { useSelector } from 'react-redux';
import { usersApi } from '~/features/user/model/api/users-api';
import ColorModeIconDropdown from '~/shared/theme/ColorModeIconDropdown';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import { deleteCookie, getCookie } from '~/shared/utils/cookie';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';

export default function SideMenuMobile({ open, toggleDrawer }: SideMenuMobileProps) {
  const { data } = useSelector(usersApi.endpoints.getUserInfo.select(undefined)) as any;
  const [logoutOpen, setLogoutOpen] = React.useState(false);
  const [tokenCopied, setTokenCopied] = React.useState(false);
  const shouldShowToken = React.useMemo(() => {
    return import.meta.env.DEV || String(import.meta.env.VITE_DEV_AUTH).toLowerCase() === 'true';
  }, []);
  const debugToken = shouldShowToken ? (getCookie('auth-token') || '') : '';

  const handleLogout = React.useCallback(() => {
    deleteCookie('auth-token');
    location.href = '/logout';
  }, []);

  const handleCopyToken = React.useCallback(async () => {
    if (!debugToken) return;
    try {
      await navigator.clipboard.writeText(debugToken);
    } catch {
      const textarea = document.createElement('textarea');
      textarea.value = debugToken;
      textarea.style.position = 'fixed';
      textarea.style.left = '-9999px';
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
    }
    setTokenCopied(true);
    setTimeout(() => setTokenCopied(false), 1200);
  }, [debugToken]);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={toggleDrawer(false)}
      sx={{
        zIndex: (theme) => theme.zIndex.drawer + 1,
        [`& .${drawerClasses.paper}`]: {
          backgroundImage: 'none',
          backgroundColor: 'background.paper',
        },
      }}
    >
      <Stack
        sx={{
          maxWidth: '70dvw',
          height: '100%',
        }}
      >
        <Stack direction="row" sx={{ p: 2, pb: 0, gap: 1, alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography component="p" variant="h6" sx={{ fontWeight: 600 }}>
            Mango CPA
          </Typography>
          <ColorModeIconDropdown />
        </Stack>
        <Divider />
        <Stack sx={{ flexGrow: 1 }}>
          <MenuContent />
          <Divider />
        </Stack>
        <Stack sx={{ p: 2, gap: 1 }}>
          <Stack>
            <Typography variant="body2" sx={{ fontWeight: 500, lineHeight: '16px' }}>
              {data?.name}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {data?.email}
            </Typography>
            {debugToken ? (
              <Tooltip title={tokenCopied ? 'Скопировано' : 'Нажмите, чтобы скопировать токен'}>
                <Typography
                  variant="caption"
                  onClick={handleCopyToken}
                  sx={{
                    display: 'block',
                    mt: 0.25,
                    color: 'text.secondary',
                    fontFamily: 'monospace',
                    cursor: 'pointer',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    maxWidth: 210,
                  }}
                >
                  token: {debugToken}
                </Typography>
              </Tooltip>
            ) : null}
          </Stack>
          <Tooltip title="Выход">
            <IconButton color="primary" onClick={() => setLogoutOpen(true)} sx={{ alignSelf: 'flex-start' }}>
              <LogoutRoundedIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>
      <Dialog open={logoutOpen} onClose={() => setLogoutOpen(false)}>
        <DialogTitle>Подтвердите выход</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Вы уверены, что хотите выйти из приложения?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLogoutOpen(false)}>Отмена</Button>
          <Button onClick={handleLogout} color="primary">Выйти</Button>
        </DialogActions>
      </Dialog>
    </Drawer>
  );
}
